import {
  insertAtIndex,
  move,
  removeIndex,
} from "@prismicio/editor-support/Array"
import {
  type LinkContent,
  type RepeatableContent,
} from "@prismicio/types-internal/lib/content"
import { type Link } from "@prismicio/types-internal/lib/customtypes"
import { useMemo } from "react"

interface UseRepeatableLinkFieldProps {
  field: Link
  content: RepeatableContent | undefined
  onContentChange: (content: RepeatableContent) => void
}

export function useRepeatableLinkField(props: UseRepeatableLinkFieldProps) {
  const { field, content, onContentChange } = props
  const { config } = field

  // avoid generating a new link key on every render
  const defaultEmptyLink = useMemo(() => getEmptyLink({ config }), [config])
  const links = content?.value.length ? content.value : [defaultEmptyLink]

  function addLink(index: number) {
    const newLinks = insertAtIndex(links, index, getEmptyLink({ config }))
    onContentChange(createRepeatableContentFromLinks(newLinks))
  }

  function updateLink(linkContent: LinkContent | undefined, index: number) {
    const newLinks = [...links]
    const emptyLink = getEmptyLink({ config, key: newLinks[index]?.key })
    newLinks[index] = linkContent ?? emptyLink
    onContentChange(createRepeatableContentFromLinks(newLinks))
  }

  function updateLinks(updatedLinks: LinkContent[]) {
    onContentChange(createRepeatableContentFromLinks(updatedLinks))
  }

  function deleteLink(index: number) {
    const newLinks = removeIndex(links, index)
    onContentChange(createRepeatableContentFromLinks(newLinks))
  }

  function moveLink(direction: "up" | "down", index: number) {
    const newLinks = move(links, index, index + getIncrement(direction))
    onContentChange(createRepeatableContentFromLinks(newLinks))
  }

  return { links, updateLink, updateLinks, addLink, deleteLink, moveLink }
}

function getIncrement(direction: "up" | "down"): number {
  switch (direction) {
    case "up":
      return -1
    case "down":
      return 1
  }
}

type GetEmptyLinkProps = {
  config: Link["config"] | undefined
  key?: string
}

function getEmptyLink(props: GetEmptyLinkProps): LinkContent {
  const { config, key = crypto.randomUUID() } = props
  const value = getLinkContent(config?.select)
  if (config?.allowText) value["text"] = ""

  return { __TYPE__: "LinkContent", key, value }
}

function getLinkContent(
  select: NonNullable<Link["config"]>["select"] | undefined,
): LinkContent["value"] {
  switch (select) {
    case "web":
      return { __TYPE__: "ExternalLink", kind: "web" }
    case "document":
      return { __TYPE__: "DocumentLink", kind: "document" }
    case "media":
      return { __TYPE__: "MediaLink", kind: "media" }
    default:
      return { __TYPE__: "AnyLink", kind: "any" }
  }
}

function createRepeatableContentFromLinks(
  value: LinkContent[],
): RepeatableContent {
  return { __TYPE__: "RepeatableContent", type: "Link", value }
}
